import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Plus, CircleCheck, CircleAlert } from 'lucide-react';
import { useToast } from '@kollez/design-system';

/**
 * /workflows/builder — direct port of kollez/pages/workflow/builder.html.
 *   Editable stage list + right-rail funnel projection + checks + score weights.
 */
interface Stage { key: string; label: string; owner: string; sla: number; weight: number; passRate: number; }

const INITIAL: Stage[] = [
  { key: 'apply', label: 'Application form', owner: 'System', sla: 0, weight: 0, passRate: 100 },
  { key: 'elig', label: 'Eligibility check', owner: 'System', sla: 1, weight: 0, passRate: 62 },
  { key: 'test', label: 'Online test', owner: 'Placement office', sla: 5, weight: 40, passRate: 38 },
  { key: 'panel', label: 'Panel interview', owner: 'Company', sla: 5, weight: 60, passRate: 22 },
  { key: 'offer', label: 'Offer', owner: 'Company', sla: 5, weight: 0, passRate: 100 },
];

const PALETTE = ['Eligibility', 'Chat screening', 'Online test', 'Work sample', 'Group discussion', 'HR interview', 'Panel interview', 'Offer'];

export function WorkflowBuilderPage() {
  const nav = useNavigate();
  const toast = useToast();
  const [stages, setStages] = useState<Stage[]>(INITIAL);
  const [cur, setCur] = useState(2);
  const [seats, setSeats] = useState(21);
  const [sla, setSla] = useState(5);

  const funnel = useMemo(() => {
    let n = 412;
    return stages.map((s) => {
      const after = Math.round((n * s.passRate) / 100);
      const row = { label: s.label, before: n, after };
      n = after;
      return row;
    });
  }, [stages]);

  const projectedOffers = funnel[funnel.length - 1]?.after ?? 0;
  const days = stages.reduce((sum, s) => sum + s.sla, 0);

  function addStage(name: string) {
    setStages((s) => [...s.slice(0, -1), { key: `s${Date.now()}`, label: name, owner: 'Placement office', sla: 5, weight: 0, passRate: 40 }, ...s.slice(-1)]);
    toast.show({ message: `Added "${name}". Adjust rules and score in the panel on the right.` });
  }

  function removeStage(i: number) {
    setStages((s) => s.filter((_, j) => j !== i));
    setCur(0);
  }

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .wb { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1180px) { .wb { grid-template-columns: minmax(0,1fr) 360px; } .wb-side { position: sticky; top: 76px; } }
        .stage { display: grid; grid-template-columns: 34px minmax(0,1fr) auto; gap: 14px; padding: 16px; align-items: start; cursor: pointer; }
        .stage[aria-current="true"] { border-color: rgb(var(--ink-accent)); box-shadow: 0 0 0 1px rgb(var(--ink-accent)); }
        .stage .n { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; background: rgb(var(--bg-info)); color: rgb(var(--ink-accent)); font-weight: 600; }
        .stage-facts { display: flex; flex-wrap: wrap; gap: 6px 14px; margin-top: 8px; font-size: .8125rem; color: rgb(var(--ink-secondary)); }
        .palette-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 10px; border: 1px dashed rgb(var(--line-secondary)); border-radius: 8px; background: rgb(var(--bg-primary)); font-size: .8125rem; cursor: pointer; }
        .palette-btn:hover { border-color: rgb(var(--ink-accent)); color: rgb(var(--ink-accent)); border-style: solid; }
        .fn-bar { height: 22px; border-radius: 4px; background: rgb(var(--ink-accent) / .16); position: relative; overflow: hidden; }
        .fn-bar span { position: absolute; inset: 0 auto 0 0; background: rgb(var(--ink-accent) / .55); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Campus drive · version 5 (draft) · owned by the placement office</p>
          <h1 className="h-page">Campus drive · standard</h1>
          <p className="subhead">Every candidate takes these steps in order. Each step says who runs it, by when, what gets them through, and what it adds to their final score.</p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/mock-report"><Eye size={14} />See it as a candidate</Link>
          <button
            className="btn btn-primary"
            disabled
            title="Workflow publish endpoint (POST /workflows/:id/publish) lands in a follow-up."
          >
            Publish v5 · soon
          </button>
        </div>
      </div>

      <div className="wb">
        <div className="grid gap-4 min-w-0">
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="grid gap-1.5"><span className="label">Used for</span><select className="select"><option>Campus drive</option><option>Internship</option><option>Hackathon or training</option><option>Admission or onboarding</option></select></label>
            <label className="grid gap-1.5"><span className="label">Who owns it</span><select className="select"><option>Placement office</option><option>Company</option><option>Department</option><option>Group office</option></select></label>
            <label className="grid gap-1.5"><span className="label">Mode</span><select className="select"><option>Over days (normal)</option><option>One day (tournament)</option></select></label>
          </div>

          <section className="card card-pad grid gap-3">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h2 className="h-section">The path</h2>
              <span className="helper">{stages.length} stages · about {days} days end-to-end</span>
            </div>
            <div className="grid gap-3">
              {stages.map((s, i) => (
                <div key={s.key} className="card stage" aria-current={cur === i} onClick={() => setCur(i)}>
                  <span className="n">{i + 1}</span>
                  <div>
                    <div className="font-medium">{s.label}</div>
                    <div className="stage-facts">
                      <span>Owner <b>{s.owner}</b></span>
                      <span>SLA <b>{s.sla}d</b></span>
                      <span>Pass rate <b>{s.passRate}%</b></span>
                      <span>Weight <b>{s.weight}%</b></span>
                    </div>
                  </div>
                  {stages.length > 2 && s.key !== 'apply' && s.key !== 'offer' && (
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); removeStage(i); }}>Remove</button>
                  )}
                </div>
              ))}
            </div>
            <div className="grid gap-2">
              <span className="label">Add a stage</span>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((p) => (
                  <button key={p} className="palette-btn" onClick={() => addStage(p)}><Plus size={12} />{p}</button>
                ))}
              </div>
            </div>
          </section>

          <section className="card card-pad grid gap-3">
            <h2 className="h-section">What every candidate is promised</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1.5">
                <span className="label">Result after each stage, within</span>
                <div className="flex items-center gap-2">
                  <input className="input" type="number" value={sla} onChange={(e) => setSla(+e.target.value)} style={{ width: 80 }} />
                  <span className="helper">days · we chase whoever is holding it up</span>
                </div>
              </label>
              <label className="grid gap-1.5">
                <span className="label">Total candidate effort, capped at</span>
                <div className="flex items-center gap-2">
                  <input className="input" type="number" defaultValue={6} style={{ width: 80 }} />
                  <span className="helper">hours across all stages</span>
                </div>
              </label>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="check mt-0.5" defaultChecked />
              <span>Everyone who doesn't get through hears why, in plain words <span className="helper block">Written by Nexie from the stage's own notes, approved by the placement officer before sending.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="check mt-0.5" defaultChecked />
              <span>Students may try again next season <span className="helper block">Scores from this workflow are kept for 6 months, then cleared.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="check mt-0.5" defaultChecked />
              <span>Extra time for students with registered accommodations, at every timed stage</span>
            </label>
          </section>
        </div>

        <aside className="wb-side grid gap-4">
          <div className="card card-pad grid gap-3">
            <div className="flex items-baseline justify-between">
              <h2 className="h-section">If 412 apply</h2>
              <span className="helper">{seats} seats</span>
            </div>
            <div className="grid gap-2">
              {funnel.map((f) => (
                <div key={f.label} className="grid items-center gap-2 text-sm" style={{ gridTemplateColumns: '1fr 64px' }}>
                  <div className="fn-bar"><span style={{ width: `${Math.max(2, (f.after / 412) * 100)}%` }} /></div>
                  <div className="text-right"><b>{f.after}</b><div className="cell-sub">{f.label}</div></div>
                </div>
              ))}
            </div>
            <div className="grid gap-1 pt-2 border-t border-line-tertiary text-sm">
              <div className="flex justify-between"><span className="text-ink-secondary">Expected offers</span><b>{projectedOffers}</b></div>
              <div className="flex justify-between"><span className="text-ink-secondary">Time from apply to offer</span><b>~{days} days</b></div>
              <div className="flex justify-between"><span className="text-ink-secondary">Candidate effort</span><b>~2 hours</b></div>
              <div className="flex justify-between"><span className="text-ink-secondary">Interview slots needed</span><b>{Math.ceil(seats * 2.5)}</b></div>
            </div>
            <label className="grid gap-1.5">
              <span className="label">Seats to fill</span>
              <input className="input" type="number" value={seats} onChange={(e) => setSeats(+e.target.value)} style={{ width: 90 }} />
            </label>
          </div>

          <div className="card card-pad grid gap-2">
            <h2 className="h-section">Checks</h2>
            <div className="qcheck ok"><CircleCheck size={14} /><span>Total weight adds to 100</span></div>
            <div className="qcheck ok"><CircleCheck size={14} /><span>SLA fits inside the season</span></div>
            {projectedOffers < seats && (
              <div className="qcheck warn"><CircleAlert size={14} /><span>Expected {projectedOffers} offers for {seats} seats. Lower a stage's pass rate or widen eligibility.</span></div>
            )}
          </div>

          <div className="card card-pad grid gap-2">
            <h2 className="h-section">Score weights</h2>
            <div className="grid gap-2">
              {stages.filter((s) => s.weight > 0).map((s, i) => (
                <div key={s.key} className="grid items-center gap-2 text-sm" style={{ gridTemplateColumns: '1fr 60px' }}>
                  <div>
                    <div>{s.label}</div>
                    <div className="bar"><span style={{ width: `${s.weight}%` }} /></div>
                  </div>
                  <input
                    className="input"
                    type="number"
                    value={s.weight}
                    onChange={(e) => setStages((ss) => ss.map((x, j) => (x.key === s.key ? { ...x, weight: +e.target.value } : x)))}
                  />
                </div>
              ))}
            </div>
            <p className="helper m-0">The final ranking. Stages with no weight decide only pass or fail.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
